/* tslint:disable */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { TFModuleListResponseDTO } from '../models/tfmodule-list-response-dto';
import { TFModule } from '../models/tfmodule';
import { TFModuleLiteDTO } from '../models/tfmodule-lite-dto';
import { ListResourcesResponse } from '../models/list-resources-response';
import { TFModuleResponseDTO } from '../models/tfmodule-response-dto';
import { ModuleMetadata } from '../models/module-metadata';
import { Resource } from '../models/resource';
import { ModuleUsageDTO } from '../models/module-usage-dto';

/**
 * Ui TF Module Controller
 */
@Injectable({
  providedIn: 'root',
})
class UiTfModuleControllerService extends __BaseService {
  static readonly getAllModulesUsingGETPath = '/cc-ui/v1/modules';
  static readonly getAllUsingGET4Path = '/cc-ui/v1/modules/all';
  static readonly bootstrapModulesUsingPOSTPath = '/cc-ui/v1/modules/bootstrap';
  static readonly getIntentAddOnModulesUsingGETPath =
    '/cc-ui/v1/modules/intent/{intent}/flavor/{flavor}/add-ons';
  static readonly markAsPublishedUsingPOSTPath =
    '/cc-ui/v1/modules/intent/{intent}/flavor/{flavor}/version/{version}/mark-published';
  static readonly getAllModulesLiteUsingGETPath =
    '/cc-ui/v1/modules/modules-lite';
  static readonly getGroupedModulesForStackUsingGETPath =
    '/cc-ui/v1/modules/stack/{stackName}/grouped';
  static readonly getModuleForIFVAndStackUsingGETPath =
    '/cc-ui/v1/modules/stack/{stackName}/{intent}/{flavor}/{version}/module';
  static readonly uploadModuleUsingPOSTPath = '/cc-ui/v1/modules/upload';
  static readonly downloadModuleByVersionIdUsingGETPath =
    '/cc-ui/v1/modules/version/{versionId}/download';
  static readonly getByIdUsingGETPath = '/cc-ui/v1/modules/{id}';
  static readonly deleteTfModuleUsingDELETEPath = '/cc-ui/v1/modules/{id}';
  static readonly downloadModuleByIdUsingGETPath =
    '/cc-ui/v1/modules/{id}/download';
  static readonly markAsPublishedByIdUsingPOSTPath =
    '/cc-ui/v1/modules/{id}/mark-published';
  static readonly getModuleUsagesUsingGETPath = '/cc-ui/v1/modules/{id}/usages';

  constructor(config: __Configuration, http: HttpClient) {
    super(config, http);
  }

  /**
   * getAllModules
   * @return OK
   */
  getAllModulesUsingGETResponse(): __Observable<
    __StrictHttpResponse<Array<TFModuleListResponseDTO>>
  > {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/cc-ui/v1/modules`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<TFModuleListResponseDTO>>;
      }),
    );
  }
  /**
   * getAllModules
   * @return OK
   */
  getAllModulesUsingGET(): __Observable<Array<TFModuleListResponseDTO>> {
    return this.getAllModulesUsingGETResponse().pipe(
      __map((_r) => _r.body as Array<TFModuleListResponseDTO>),
    );
  }

  /**
   * Get All Modules
   *
   * - **Description:** Retrieves all modules with optional filtering.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param params The `UiTfModuleControllerService.GetAllUsingGET4Params` containing the following parameters:
   *
   * - `withPathOnly`: withPathOnly
   *
   * - `includeFacetsYaml`: includeFacetsYaml
   *
   * - `allowPreviewModules`: allowPreviewModules
   *
   * @return Operation successful
   */
  getAllUsingGET4Response(
    params: UiTfModuleControllerService.GetAllUsingGET4Params,
  ): __Observable<__StrictHttpResponse<Array<TFModule>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.withPathOnly != null)
      __params = __params.set('withPathOnly', params.withPathOnly.toString());
    if (params.includeFacetsYaml != null)
      __params = __params.set(
        'includeFacetsYaml',
        params.includeFacetsYaml.toString(),
      );
    if (params.allowPreviewModules != null)
      __params = __params.set(
        'allowPreviewModules',
        params.allowPreviewModules.toString(),
      );
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/cc-ui/v1/modules/all`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<TFModule>>;
      }),
    );
  }
  /**
   * Get All Modules
   *
   * - **Description:** Retrieves all modules with optional filtering.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param params The `UiTfModuleControllerService.GetAllUsingGET4Params` containing the following parameters:
   *
   * - `withPathOnly`: withPathOnly
   *
   * - `includeFacetsYaml`: includeFacetsYaml
   *
   * - `allowPreviewModules`: allowPreviewModules
   *
   * @return Operation successful
   */
  getAllUsingGET4(
    params: UiTfModuleControllerService.GetAllUsingGET4Params,
  ): __Observable<Array<TFModule>> {
    return this.getAllUsingGET4Response(params).pipe(
      __map((_r) => _r.body as Array<TFModule>),
    );
  }

  /**
   * Bootstrap Modules
   *
   * - **Description:** Bootstraps modules for initialization.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @return Operation successful
   */
  bootstrapModulesUsingPOSTResponse(): __Observable<
    __StrictHttpResponse<{ [key: string]: Array<string> }>
  > {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/cc-ui/v1/modules/bootstrap`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<{ [key: string]: Array<string> }>;
      }),
    );
  }
  /**
   * Bootstrap Modules
   *
   * - **Description:** Bootstraps modules for initialization.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @return Operation successful
   */
  bootstrapModulesUsingPOST(): __Observable<{ [key: string]: Array<string> }> {
    return this.bootstrapModulesUsingPOSTResponse().pipe(
      __map((_r) => _r.body as { [key: string]: Array<string> }),
    );
  }

  /**
   * Get Add-On Modules
   *
   * - **Description:** Retrieves all add-on modules based on intent and flavor, optionally by cloud.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param params The `UiTfModuleControllerService.GetIntentAddOnModulesUsingGETParams` containing the following parameters:
   *
   * - `intent`: intent
   *
   * - `flavor`: flavor
   *
   * - `cloud`: cloud
   *
   * @return Operation successful
   */
  getIntentAddOnModulesUsingGETResponse(
    params: UiTfModuleControllerService.GetIntentAddOnModulesUsingGETParams,
  ): __Observable<__StrictHttpResponse<Array<TFModuleLiteDTO>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.cloud != null)
      __params = __params.set('cloud', params.cloud.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/modules/intent/${encodeURIComponent(
          String(params.intent),
        )}/flavor/${encodeURIComponent(String(params.flavor))}/add-ons`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<TFModuleLiteDTO>>;
      }),
    );
  }
  /**
   * Get Add-On Modules
   *
   * - **Description:** Retrieves all add-on modules based on intent and flavor, optionally by cloud.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param params The `UiTfModuleControllerService.GetIntentAddOnModulesUsingGETParams` containing the following parameters:
   *
   * - `intent`: intent
   *
   * - `flavor`: flavor
   *
   * - `cloud`: cloud
   *
   * @return Operation successful
   */
  getIntentAddOnModulesUsingGET(
    params: UiTfModuleControllerService.GetIntentAddOnModulesUsingGETParams,
  ): __Observable<Array<TFModuleLiteDTO>> {
    return this.getIntentAddOnModulesUsingGETResponse(params).pipe(
      __map((_r) => _r.body as Array<TFModuleLiteDTO>),
    );
  }

  /**
   * Mark Module as Published
   *
   * - **Description:** Marks a specific module version as published.
   * - **Permissions:** Requires MODULE_WRITE permission.
   * - **Audit Logging:** Yes
   * @param params The `UiTfModuleControllerService.MarkAsPublishedUsingPOSTParams` containing the following parameters:
   *
   * - `version`: version
   *
   * - `intent`: intent
   *
   * - `flavor`: flavor
   *
   * @return OK
   */
  markAsPublishedUsingPOSTResponse(
    params: UiTfModuleControllerService.MarkAsPublishedUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<TFModule>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/modules/intent/${encodeURIComponent(
          String(params.intent),
        )}/flavor/${encodeURIComponent(
          String(params.flavor),
        )}/version/${encodeURIComponent(
          String(params.version),
        )}/mark-published`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TFModule>;
      }),
    );
  }
  /**
   * Mark Module as Published
   *
   * - **Description:** Marks a specific module version as published.
   * - **Permissions:** Requires MODULE_WRITE permission.
   * - **Audit Logging:** Yes
   * @param params The `UiTfModuleControllerService.MarkAsPublishedUsingPOSTParams` containing the following parameters:
   *
   * - `version`: version
   *
   * - `intent`: intent
   *
   * - `flavor`: flavor
   *
   * @return OK
   */
  markAsPublishedUsingPOST(
    params: UiTfModuleControllerService.MarkAsPublishedUsingPOSTParams,
  ): __Observable<TFModule> {
    return this.markAsPublishedUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as TFModule),
    );
  }

  /**
   * getAllModulesLite
   * @param params The `UiTfModuleControllerService.GetAllModulesLiteUsingGETParams` containing the following parameters:
   *
   * - `clouds`: clouds
   *
   * - `allowPreviewModules`: allowPreviewModules
   *
   * @return OK
   */
  getAllModulesLiteUsingGETResponse(
    params: UiTfModuleControllerService.GetAllModulesLiteUsingGETParams,
  ): __Observable<__StrictHttpResponse<Array<TFModuleLiteDTO>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    (params.clouds || []).forEach((val) => {
      if (val != null) __params = __params.append('clouds', val.toString());
    });
    if (params.allowPreviewModules != null)
      __params = __params.set(
        'allowPreviewModules',
        params.allowPreviewModules.toString(),
      );
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/cc-ui/v1/modules/modules-lite`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<TFModuleLiteDTO>>;
      }),
    );
  }
  /**
   * getAllModulesLite
   * @param params The `UiTfModuleControllerService.GetAllModulesLiteUsingGETParams` containing the following parameters:
   *
   * - `clouds`: clouds
   *
   * - `allowPreviewModules`: allowPreviewModules
   *
   * @return OK
   */
  getAllModulesLiteUsingGET(
    params: UiTfModuleControllerService.GetAllModulesLiteUsingGETParams,
  ): __Observable<Array<TFModuleLiteDTO>> {
    return this.getAllModulesLiteUsingGETResponse(params).pipe(
      __map((_r) => _r.body as Array<TFModuleLiteDTO>),
    );
  }

  /**
   * Get Grouped Modules for Stack
   *
   * - **Description:** Retrieves grouped modules specifically for a stack name.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param stackName stackName
   * @return Operation successful
   */
  getGroupedModulesForStackUsingGETResponse(
    stackName: string,
  ): __Observable<__StrictHttpResponse<ListResourcesResponse>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/modules/stack/${encodeURIComponent(
          String(stackName),
        )}/grouped`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ListResourcesResponse>;
      }),
    );
  }
  /**
   * Get Grouped Modules for Stack
   *
   * - **Description:** Retrieves grouped modules specifically for a stack name.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param stackName stackName
   * @return Operation successful
   */
  getGroupedModulesForStackUsingGET(
    stackName: string,
  ): __Observable<ListResourcesResponse> {
    return this.getGroupedModulesForStackUsingGETResponse(stackName).pipe(
      __map((_r) => _r.body as ListResourcesResponse),
    );
  }

  /**
   * getModuleForIFVAndStack
   * @param params The `UiTfModuleControllerService.GetModuleForIFVAndStackUsingGETParams` containing the following parameters:
   *
   * - `version`: version
   *
   * - `stackName`: stackName
   *
   * - `intent`: intent
   *
   * - `flavor`: flavor
   *
   * @return OK
   */
  getModuleForIFVAndStackUsingGETResponse(
    params: UiTfModuleControllerService.GetModuleForIFVAndStackUsingGETParams,
  ): __Observable<__StrictHttpResponse<TFModuleResponseDTO>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/modules/stack/${encodeURIComponent(
          String(params.stackName),
        )}/${encodeURIComponent(String(params.intent))}/${encodeURIComponent(
          String(params.flavor),
        )}/${encodeURIComponent(String(params.version))}/module`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TFModuleResponseDTO>;
      }),
    );
  }
  /**
   * getModuleForIFVAndStack
   * @param params The `UiTfModuleControllerService.GetModuleForIFVAndStackUsingGETParams` containing the following parameters:
   *
   * - `version`: version
   *
   * - `stackName`: stackName
   *
   * - `intent`: intent
   *
   * - `flavor`: flavor
   *
   * @return OK
   */
  getModuleForIFVAndStackUsingGET(
    params: UiTfModuleControllerService.GetModuleForIFVAndStackUsingGETParams,
  ): __Observable<TFModuleResponseDTO> {
    return this.getModuleForIFVAndStackUsingGETResponse(params).pipe(
      __map((_r) => _r.body as TFModuleResponseDTO),
    );
  }

  /**
   * Upload a module
   *
   * - **Description:** Uploads a module using a file.
   * - **Permissions:** Requires MODULE_WRITE permission.
   * - **Parameters:**
   *   - `file`: The module file to upload
   *   - `metadata` (optional): Additional module metadata including:
   *     - `gitUrl`: Web URL of the git repository (Expected to embed the commit id)
   *     - `gitRef`: Git reference (branch, tag, or commit)
   *     - `isFeatureBranch`: If this is true, this preview module cannot be directly marked as published until we register the module again with this as false.
   * - **Audit Logging:** Yes
   * @param params The `UiTfModuleControllerService.UploadModuleUsingPOSTParams` containing the following parameters:
   *
   * - `file`: file
   *
   * - `metadata`: metadata
   *
   * @return OK
   */
  uploadModuleUsingPOSTResponse(
    params: UiTfModuleControllerService.UploadModuleUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<TFModule>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let __formData = new FormData();
    __body = __formData;
    if (params.file != null) {
      __formData.append('file', params.file as string | Blob);
    }
    if (params.metadata != null) {
      __formData.append('metadata', JSON.stringify(params.metadata));
    }
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/cc-ui/v1/modules/upload`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TFModule>;
      }),
    );
  }
  /**
   * Upload a module
   *
   * - **Description:** Uploads a module using a file.
   * - **Permissions:** Requires MODULE_WRITE permission.
   * - **Parameters:**
   *   - `file`: The module file to upload
   *   - `metadata` (optional): Additional module metadata including:
   *     - `gitUrl`: Web URL of the git repository (Expected to embed the commit id)
   *     - `gitRef`: Git reference (branch, tag, or commit)
   *     - `isFeatureBranch`: If this is true, this preview module cannot be directly marked as published until we register the module again with this as false.
   * - **Audit Logging:** Yes
   * @param params The `UiTfModuleControllerService.UploadModuleUsingPOSTParams` containing the following parameters:
   *
   * - `file`: file
   *
   * - `metadata`: metadata
   *
   * @return OK
   */
  uploadModuleUsingPOST(
    params: UiTfModuleControllerService.UploadModuleUsingPOSTParams,
  ): __Observable<TFModule> {
    return this.uploadModuleUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as TFModule),
    );
  }

  /**
   * downloadModuleByVersionId
   * @param versionId versionId
   * @return OK
   */
  downloadModuleByVersionIdUsingGETResponse(
    versionId: string,
  ): __Observable<__StrictHttpResponse<Resource>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/modules/version/${encodeURIComponent(
          String(versionId),
        )}/download`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Resource>;
      }),
    );
  }
  /**
   * downloadModuleByVersionId
   * @param versionId versionId
   * @return OK
   */
  downloadModuleByVersionIdUsingGET(versionId: string): __Observable<Resource> {
    return this.downloadModuleByVersionIdUsingGETResponse(versionId).pipe(
      __map((_r) => _r.body as Resource),
    );
  }

  /**
   * Get Module by ID
   *
   * - **Description:** Retrieves a module by its ID.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param id id
   * @return Operation successful
   */
  getByIdUsingGETResponse(
    id: string,
  ): __Observable<__StrictHttpResponse<TFModuleResponseDTO>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/cc-ui/v1/modules/${encodeURIComponent(String(id))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TFModuleResponseDTO>;
      }),
    );
  }
  /**
   * Get Module by ID
   *
   * - **Description:** Retrieves a module by its ID.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param id id
   * @return Operation successful
   */
  getByIdUsingGET(id: string): __Observable<TFModuleResponseDTO> {
    return this.getByIdUsingGETResponse(id).pipe(
      __map((_r) => _r.body as TFModuleResponseDTO),
    );
  }

  /**
   * Delete a Module
   *
   * - **Description:** Deletes a module by ID.
   * - **Permissions:** Requires MODULE_DELETE permission.
   * - **Audit Logging:** Yes
   * @param params The `UiTfModuleControllerService.DeleteTfModuleUsingDELETEParams` containing the following parameters:
   *
   * - `id`: id
   *
   * - `force`: force
   *
   * @return Operation successful
   */
  deleteTfModuleUsingDELETEResponse(
    params: UiTfModuleControllerService.DeleteTfModuleUsingDELETEParams,
  ): __Observable<__StrictHttpResponse<TFModule>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.force != null)
      __params = __params.set('force', params.force.toString());
    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl +
        `/cc-ui/v1/modules/${encodeURIComponent(String(params.id))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TFModule>;
      }),
    );
  }
  /**
   * Delete a Module
   *
   * - **Description:** Deletes a module by ID.
   * - **Permissions:** Requires MODULE_DELETE permission.
   * - **Audit Logging:** Yes
   * @param params The `UiTfModuleControllerService.DeleteTfModuleUsingDELETEParams` containing the following parameters:
   *
   * - `id`: id
   *
   * - `force`: force
   *
   * @return Operation successful
   */
  deleteTfModuleUsingDELETE(
    params: UiTfModuleControllerService.DeleteTfModuleUsingDELETEParams,
  ): __Observable<TFModule> {
    return this.deleteTfModuleUsingDELETEResponse(params).pipe(
      __map((_r) => _r.body as TFModule),
    );
  }

  /**
   * downloadModuleById
   * @param id id
   * @return OK
   */
  downloadModuleByIdUsingGETResponse(
    id: string,
  ): __Observable<__StrictHttpResponse<Resource>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/modules/${encodeURIComponent(String(id))}/download`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Resource>;
      }),
    );
  }
  /**
   * downloadModuleById
   * @param id id
   * @return OK
   */
  downloadModuleByIdUsingGET(id: string): __Observable<Resource> {
    return this.downloadModuleByIdUsingGETResponse(id).pipe(
      __map((_r) => _r.body as Resource),
    );
  }

  /**
   * Mark Module as Published
   *
   * - **Description:** Marks a specific module version as published.
   * - **Permissions:** Requires MODULE_WRITE permission.
   * - **Audit Logging:** Yes
   * @param id id
   * @return OK
   */
  markAsPublishedByIdUsingPOSTResponse(
    id: string,
  ): __Observable<__StrictHttpResponse<TFModule>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/modules/${encodeURIComponent(String(id))}/mark-published`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TFModule>;
      }),
    );
  }
  /**
   * Mark Module as Published
   *
   * - **Description:** Marks a specific module version as published.
   * - **Permissions:** Requires MODULE_WRITE permission.
   * - **Audit Logging:** Yes
   * @param id id
   * @return OK
   */
  markAsPublishedByIdUsingPOST(id: string): __Observable<TFModule> {
    return this.markAsPublishedByIdUsingPOSTResponse(id).pipe(
      __map((_r) => _r.body as TFModule),
    );
  }

  /**
   * Get Module Usages
   *
   * - **Description:** Retrieves all usages of a module across stacks and clusters.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param id id
   * @return Operation successful
   */
  getModuleUsagesUsingGETResponse(
    id: string,
  ): __Observable<__StrictHttpResponse<Array<ModuleUsageDTO>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/modules/${encodeURIComponent(String(id))}/usages`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<ModuleUsageDTO>>;
      }),
    );
  }
  /**
   * Get Module Usages
   *
   * - **Description:** Retrieves all usages of a module across stacks and clusters.
   * - **Audit Logging:** No specific audit logging at the moment.
   * @param id id
   * @return Operation successful
   */
  getModuleUsagesUsingGET(id: string): __Observable<Array<ModuleUsageDTO>> {
    return this.getModuleUsagesUsingGETResponse(id).pipe(
      __map((_r) => _r.body as Array<ModuleUsageDTO>),
    );
  }
}

module UiTfModuleControllerService {
  /**
   * Parameters for getAllUsingGET4
   */
  export interface GetAllUsingGET4Params {
    /**
     * withPathOnly
     */
    withPathOnly?: boolean;

    /**
     * includeFacetsYaml
     */
    includeFacetsYaml?: boolean;

    /**
     * allowPreviewModules
     */
    allowPreviewModules?: boolean;
  }

  /**
   * Parameters for getIntentAddOnModulesUsingGET
   */
  export interface GetIntentAddOnModulesUsingGETParams {
    /**
     * intent
     */
    intent: string;

    /**
     * flavor
     */
    flavor: string;

    /**
     * cloud
     */
    cloud?: string;
  }

  /**
   * Parameters for markAsPublishedUsingPOST
   */
  export interface MarkAsPublishedUsingPOSTParams {
    /**
     * version
     */
    version: string;

    /**
     * intent
     */
    intent: string;

    /**
     * flavor
     */
    flavor: string;
  }

  /**
   * Parameters for getAllModulesLiteUsingGET
   */
  export interface GetAllModulesLiteUsingGETParams {
    /**
     * clouds
     */
    clouds?: Array<
      'AWS' | 'AZURE' | 'LOCAL' | 'GCP' | 'KUBERNETES' | 'NO_CLOUD'
    >;

    /**
     * allowPreviewModules
     */
    allowPreviewModules?: boolean;
  }

  /**
   * Parameters for getModuleForIFVAndStackUsingGET
   */
  export interface GetModuleForIFVAndStackUsingGETParams {
    /**
     * version
     */
    version: string;

    /**
     * stackName
     */
    stackName: string;

    /**
     * intent
     */
    intent: string;

    /**
     * flavor
     */
    flavor: string;
  }

  /**
   * Parameters for uploadModuleUsingPOST
   */
  export interface UploadModuleUsingPOSTParams {
    /**
     * file
     */
    file: Blob;

    /**
     * metadata
     */
    metadata?: ModuleMetadata;
  }

  /**
   * Parameters for deleteTfModuleUsingDELETE
   */
  export interface DeleteTfModuleUsingDELETEParams {
    /**
     * id
     */
    id: string;

    /**
     * force
     */
    force?: boolean;
  }
}

export { UiTfModuleControllerService };
